<template><div><code-well-header>
  <div class="d-w50p">
    <dt-skeleton :animate="false" />
  </div>
</code-well-header>
<h2 id="skeleton-vs-loader" tabindex="-1" class="d-docsite--header-2">Skeleton vs Loader <a class="header-anchor d-link d-docsite--link d-link" href="#skeleton-vs-loader" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph"><strong>Use Skeleton when:</strong></p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Content is expected to take more than a few hundred milliseconds to load on average</li>
<li class="d-docsite--list-element">You want to show the approximate layout and structure of content being loaded</li>
<li class="d-docsite--list-element">Loading complex lists, cards, or detailed content where users benefit from seeing the expected layout</li>
<li class="d-docsite--list-element">You need to maintain visual hierarchy during loading states</li>
</ul>
<p class="d-docsite--paragraph"><strong>Use <a href="/components/loader.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Loader</a> when:</strong></p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">You need a simple, generic loading indicator</li>
<li class="d-docsite--list-element">Loading time is short or indeterminate</li>
<li class="d-docsite--list-element">You want to indicate that a process is running without showing layout structure</li>
<li class="d-docsite--list-element">You need a compact loading indicator for buttons, small components, or overlays</li>
</ul>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use when data takes more than 300ms to load on an average connection for our user base.</li>
<li class="d-docsite--list-element">Use to represent a general layout of what is being loaded.</li>
<li class="d-docsite--list-element">For a repeating list, show 6 items maximum, since it’s enough to give an idea of a layout. For conversations, limit to 3.</li>
<li class="d-docsite--list-element">Use skeleton loading for dynamic content, and use actual content for static content that doesn’t change e.g. page title, headings, action components (button, toggles, checkboxes etc.).</li>
<li class="d-docsite--list-element">Dynamic content: Content that would change after loading, usually data-based text.</li>
<li class="d-docsite--list-element">Static content: Content that can be loaded quickly and wouldn’t change, usually non-data-based text.</li>
<li class="d-docsite--list-element">Always match the size of content that will load.</li>
<li class="d-docsite--list-element">Always allow content to load gradually. Real content should replace skeleton objects immediately when the data is available.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use for fast loading less than 300ms.</li>
<li class="d-docsite--list-element">Be super detailed trying to represent everything in an interface.</li>
<li class="d-docsite--list-element">Use for user action feedback e.g. saving form.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-w50p">
    <dt-skeleton :animate="false" arial-label="Loading" ref="defaultExample" />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.defaultExample"
vueCode='
<dt-skeleton :animate="false" arial-label="Loading" />
'
showHtmlWarning />
<h3 id="animation" tabindex="-1" class="d-docsite--header-3">Animation <a class="header-anchor d-link d-docsite--link d-link" href="#animation" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-w50p">
    <dt-skeleton arial-label="Loading" ref="animationExample" />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.animationExample"
vueCode='
<dt-skeleton arial-label="Loading" />
'
/>
<h2 id="custom" tabindex="-1" class="d-docsite--header-2">Custom <a class="header-anchor d-link d-docsite--link d-link" href="#custom" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">To customize a non-animating Skeleton background color modify the <code v-pre>--placeholder-from-color</code> variable with an inline <code v-pre>style</code>.</p>
<code-well-header>
  <div class="d-w50p">
    <dt-skeleton
      :animate="false"
      :text-option="{
        style: '--placeholder-from-color: var(--dt-color-blue-400)',
      }"
      ref="customExample"
      arial-label="Loading"
    />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.customExample"
vueCode='
<dt-skeleton
  :animate="false"
  :text-option="{
    style: `--placeholder-from-color: var(--dt-color-blue-400)`,
  }"
  arial-label="Loading"
/>
'
showHtmlWarning />
<p class="d-docsite--paragraph">Customize an animating Skeleton by modifying the <code v-pre>--placeholder-from-color</code> and <code v-pre>--placeholder-to-color</code> variables with an inline <code v-pre>style</code>.</p>
<code-well-header>
  <div class="d-w50p">
    <dt-skeleton
      :text-option="{
        style: '--placeholder-from-color: var(--dt-color-blue-400); --placeholder-to-color: var(--dt-color-blue-200);',
      }"
      ref="customAnimateExample"
      arial-label="Loading"
    />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.customAnimateExample"
vueCode='
<dt-skeleton
  :text-option="{
    style: `--placeholder-from-color: var(--dt-color-blue-400); --placeholder-to-color: var(--dt-color-blue-200);`,
  }"
  arial-label="Loading"
/>
'
showHtmlWarning />
<h2 id="shapes" tabindex="-1" class="d-docsite--header-2">Shapes <a class="header-anchor d-link d-docsite--link d-link" href="#shapes" aria-hidden="true">#</a></h2>
<h3 id="avatar" tabindex="-1" class="d-docsite--header-3">Avatar <a class="header-anchor d-link d-docsite--link d-link" href="#avatar" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Default sizes match the avatar size. Size is customizable when needed.</p>
<code-well-header class="d-ai-flex-start">
  <div ref="avatarExample">
    <p>S (24x24px)</p>
    <dt-skeleton :shape-option="{ shape: 'circle', size: 'sm' }" :animate="false" />
    <p>M (32x32px)</p>
    <dt-skeleton :shape-option="{ shape: 'circle' }" :animate="false" />
    <p>L (48x48px)</p>
    <dt-skeleton :shape-option="{ shape: 'circle', size: 'lg' }" :animate="false" />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.avatarExample"
vueCode='
<dt-skeleton :shape-option="{ shape: `circle`, size: `sm` }" :animate="false" />
<dt-skeleton :shape-option="{ shape: `circle` }" :animate="false" />
<dt-skeleton :shape-option="{ shape: `circle`, size: `lg` }" :animate="false" />
'
/>
<h3 id="image-icon" tabindex="-1" class="d-docsite--header-3">Image / Icon <a class="header-anchor d-link d-docsite--link d-link" href="#image-icon" aria-hidden="true">#</a></h3>
<code-well-header class="d-ai-flex-start">
  <div ref="squareExample">
    <p>S (24x24px)</p>
    <dt-skeleton :shape-option="{ shape: 'square', size: 'sm' }" :animate="false" />
    <p>M (32x32px)</p>
    <dt-skeleton :shape-option="{ shape: 'square' }" :animate="false" />
    <p>L (48x48px)</p>
    <dt-skeleton :shape-option="{ shape: 'square', size: 'lg' }" :animate="false" />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.squareExample"
vueCode='
<dt-skeleton :shape-option="{ shape: `square`, size: `sm` }" :animate="false" />
<dt-skeleton :shape-option="{ shape: `square` }" :animate="false" />
<dt-skeleton :shape-option="{ shape: `square`, size: `lg` }" :animate="false" />
'
/>
<h3 id="headings" tabindex="-1" class="d-docsite--header-3">Headings <a class="header-anchor d-link d-docsite--link d-link" href="#headings" aria-hidden="true">#</a></h3>
<code-well-header class="d-ai-flex-start">
  <div ref="headingsExample">
    <p>Small</p>
    <dt-skeleton :text-option="{ type: 'heading', headingHeight: 'sm', width: '160px' }" :animate="false" />
    <p>Medium</p>
    <dt-skeleton :text-option="{ type: 'heading', width: '240px' }" :animate="false" />
    <p>Large</p>
    <dt-skeleton :text-option="{ type: 'heading', headingHeight: 'lg', width: '320px' }" :animate="false" />
  </div>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.headingsExample"
vueCode='
<dt-skeleton :text-option="{ type: `heading`, headingHeight: `sm`, width: `160px` }" :animate="false" />
<dt-skeleton :text-option="{ type: `heading`, width: `240px` }" :animate="false" />
<dt-skeleton :text-option="{ type: `heading`, headingHeight: `lg`, width: `320px` }" :animate="false" />
'
/>
<h2 id="prefabricated-combinations" tabindex="-1" class="d-docsite--header-2">Prefabricated Combinations <a class="header-anchor d-link d-docsite--link d-link" href="#prefabricated-combinations" aria-hidden="true">#</a></h2>
<h3 id="paragraphs" tabindex="-1" class="d-docsite--header-3">Paragraphs <a class="header-anchor d-link d-docsite--link d-link" href="#paragraphs" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-w50p">
    <dt-skeleton :paragraph-option="{ rows: 5, randomWidth: true }" :animate="false" />
  </div>
</code-well-header>
<code-example-tabs
vueCode='
<dt-skeleton
  :paragraph-option="{
    rows: 5,
    randomWidth: true,
  }"
  :animate="false"
/>
'
/>
<h3 id="avatar-name" tabindex="-1" class="d-docsite--header-3">Avatar + Name <a class="header-anchor d-link d-docsite--link d-link" href="#avatar-name" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-w50p">
    <dt-skeleton
      :list-item-option="{ shapeSize: 'sm', paragraphs: { rows: 1 } }"
    />
  </div>
</code-well-header>
<code-example-tabs
vueCode='
<dt-skeleton
  :list-item-option="{
    shapeSize: `sm`,
    paragraphs: {
      rows: 1,
    },
  }"
/>
'
/>
<h3 id="icon-text" tabindex="-1" class="d-docsite--header-3">Icon + Text <a class="header-anchor d-link d-docsite--link d-link" href="#icon-text" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-w50p">
    <dt-skeleton
      :list-item-option="{
        shapeSize: '2rem',
        shape: 'square',
        paragraphs: {
          rows: 1,
        },
      }"
    />
  </div>
</code-well-header>
<code-example-tabs
vueCode='
<dt-skeleton
  :list-item-option="{
    shapeSize: `2rem`,
    shape: `square`,
    paragraphs: {
      rows: 1,
    },
  }"
/>
'
/>
<h3 id="messages-transcript-comment" tabindex="-1" class="d-docsite--header-3">Messages / Transcript / Comment <a class="header-anchor d-link d-docsite--link d-link" href="#messages-transcript-comment" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-w50p">
    <dt-skeleton
      :list-item-option="{
        shapeSize: '3.6rem',
        paragraphs: {
          rows: 4,
          width: [
            '120px', '311px', '371px', '279px',
          ],
        },
      }"
    />
  </div>
</code-well-header>
<code-example-tabs
vueCode='
<dt-skeleton
  :list-item-option="{
    shapeSize: `3.6rem`,
    paragraphs: {
      rows: 4,
      width: [
        `120px`, `311px`, `371px`, `279px`,
      ],
    },
  }"
/>
'
/>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">For sighted users, they are able to see that there is loading content and no other action is needed until loading completes.
Likewise, there is nothing that needs to be added for keyboard users.
For users who rely on assistive technology, skeleton’s visual representation of a loading state won’t be accessible
without additional labeling.</p>
<p class="d-docsite--paragraph">It's <code v-pre>role=&quot;status&quot;</code> on the skeleton component, keep in mind, that:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">The aria live region role of status has an implicit <code v-pre>aria-live</code> value of <code v-pre>polite</code>, which allows a user to be notified
via AT (such as a screen reader) when status messages are added. The <code v-pre>role</code> of <code v-pre>status</code> also has a default <code v-pre>aria-atomic</code>
value of <code v-pre>true</code>, so that updates to the container marked with a role of status will result in the AT presenting the
entire contents of the container to the user, including any author-defined labels (or additional nested elements).</p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph"><code v-pre>aria-busy</code> state indicates an element is being modified and that assistive technologies may want to wait until the
changes are complete before informing the user about the update.
When multiple parts of a live region need to be loaded before changes are announced to the user, set <code v-pre>aria-busy=&quot;true&quot;</code>
until loading is complete. Then set to aria-busy=&quot;false&quot;. This prevents assistive technologies from announcing changes
before updates are done.</p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">If you need fully accessible skeleton and loading content, probably you will want to add <code v-pre>aria-live=&quot;polite&quot;</code>
and <code v-pre>aria-busy=&quot;false&quot;</code> to corresponding loaded html element content.</p>
</li>
</ul>
<div class="language-vue" data-highlighter="prismjs" data-ext="vue"><pre v-pre><code class="language-vue"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>dt-skeleton</span></span>
<span class="line">  <span class="token attr-name">v-if</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>loading<span class="token punctuation">"</span></span></span>
<span class="line">  <span class="token attr-name">:aria-label</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>$i18n('Loading')<span class="token punctuation">"</span></span></span>
<span class="line"><span class="token punctuation">/></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span></span>
<span class="line">  <span class="token attr-name">v-else</span></span>
<span class="line">  <span class="token attr-name">aria-live</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>polite<span class="token punctuation">"</span></span></span>
<span class="line">  <span class="token attr-name">aria-busy</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>false<span class="token punctuation">"</span></span></span>
<span class="line"><span class="token punctuation">></span></span></span>
<span class="line">  Loaded content.</span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h3 id="references" tabindex="-1" class="d-docsite--header-3">References <a class="header-anchor d-link d-docsite--link d-link" href="#references" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><a href="https://accessibilityresources.org/aria-busy" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Aria-busy</a>,
<a href="https://www.digitala11y.com/status-role/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Status role</a>,
<a href="https://www.w3.org/WAI/WCAG21/Techniques/aria/ARIA22" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">ARIA22</a>,
<a href="https://a11ysupport.io/tech/aria/status_role#support-table-1" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">a11y support status_role</a></li>
<li class="d-docsite--list-element"><a href="https://www.carbondesignsystem.com/patterns/loading-pattern/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Carbon DS Loading pattern</a>,
<a href="https://uxdesign.cc/what-you-should-know-about-skeleton-screens-a820c45a571a" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Skeleton screens</a></li>
<li class="d-docsite--list-element"><a href="https://www.tpgi.com/short-note-on-being-busy/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Short note on being busy</a>,
<a href="https://adrianroselli.com/2020/11/more-accessible-skeletons.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">More Accessible Skeletons</a></li>
</ul>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="skeleton" /></div></template>


